/**
 * "Add as Lead" for one license: priority, an optional note, and the create call.
 *
 * Shared by the license page and Construction Deadlines. The Search page keeps its
 * own copy for now because it also drives per-row status for its whole results
 * table. Callers can pre-fill the note and priority with what they already know,
 * e.g. the construction deadline that made someone worth tracking.
 */
import { useState } from 'react'
import Modal, { ModalFooter } from './Modal'
import { createTrackedLead, type TrackedLead } from '../services/crm'

type Priority = 'low' | 'medium' | 'high' | 'urgent'

interface AddLeadModalProps {
  usi: number
  /** Shown under the title, e.g. "WSKQ948 — Fort Morgan Schools". */
  subtitle: string
  initialNote?: string
  initialPriority?: Priority
  onClose: () => void
  onAdded: (lead: TrackedLead) => void
}

export default function AddLeadModal({
  usi, subtitle, initialNote = '', initialPriority = 'medium', onClose, onAdded,
}: AddLeadModalProps) {
  const [priority, setPriority] = useState<Priority>(initialPriority)
  const [note, setNote] = useState(initialNote)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleAdd() {
    setLoading(true)
    setError('')
    try {
      const lead = await createTrackedLead({
        fcc_unique_system_identifier: usi,
        priority,
        note: note.trim() || undefined,
        source: 'manual',
      })
      onAdded(lead)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add lead')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      titleId="add-lead-modal-title"
      title="Add as Lead"
      subtitle={subtitle}
      onClose={onClose}
      maxWidth="max-w-md"
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="add-lead-priority" className="label text-sm">Priority</label>
          <select
            id="add-lead-priority"
            className="input"
            value={priority}
            onChange={(e) => setPriority(e.target.value as Priority)}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <div>
          <label htmlFor="add-lead-note" className="label text-sm">Note (optional)</label>
          <textarea
            id="add-lead-note"
            className="input"
            rows={3}
            placeholder="Why are you tracking this lead? Any relevant context…"
            maxLength={5000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      </div>

      <ModalFooter>
        <button type="button" onClick={onClose} className="btn btn-secondary" disabled={loading}>
          Cancel
        </button>
        <button type="button" onClick={handleAdd} className="btn btn-primary" disabled={loading}>
          {loading ? 'Adding…' : 'Add as Lead'}
        </button>
      </ModalFooter>
    </Modal>
  )
}
